import React, { useState, useEffect } from 'react';
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Layout from '../../components/Layout';
import api from '../../config/api';
import EyeIcon from '../../components/Icons/EyeIcon';
import EyeSlashIcon from '../../components/Icons/EyeSlashIcon';
import TickIcon from '../../components/Icons/TickIcon';

import {
  Container,
  Input,
  InputPasswordContainer,
  EyeButton,
  InputLabel,
  Button,
  Form,
  ErrorText,
  Header,
  Description,
  WarnContainer,
  WarnBody,
  BoldText,
  PasswordRequirement,
  PasswordRequirementContainer,
  StyledTickIcon,
  TickContainer
} from './styles';

const ChangePassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasMinLength, setHasMinLength] = useState(false);
  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasLowercase, setHasLowercase] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);
  const { userId, refreshUser, isFirstLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setErrorMessage('');
  }, [password, confirmPassword]);

  useEffect(() => {
    if (!Number(isFirstLogin)) {
      navigate('/');
    }
  }, [isFirstLogin, navigate]);

  const isPasswordValid =
    hasMinLength &&
    hasUppercase &&
    hasLowercase &&
    hasNumber &&
    hasSpecialChar;

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
  
    setHasMinLength(value.length >= 8);
    setHasUppercase(/[A-Z]/.test(value));
    setHasLowercase(/[a-z]/.test(value));
    setHasNumber(/\d/.test(value));
    setHasSpecialChar(/[^A-Za-z\d]/.test(value));
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
  };

  const validatePassword = (password) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
    return passwordRegex.test(password);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!validatePassword(password)) {
      setErrorMessage(
        "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character."
      );
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Password and Confirm Password must be the same");
      return;
    }

    try {
      const response = await api.put(
        `${process.env.REACT_APP_API_URL}/api/users/change-password/${userId}`,
        { password }
      );

      if (response.status === 200) {
        if (isFirstLogin) {
          await api.put(
            `${process.env.REACT_APP_API_URL}/api/users/first-login/${userId}`,
            {}
          );
        }
        await refreshUser();
        navigate("/");
      } else {
        setErrorMessage("Could not update password. Try again later.");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      setErrorMessage("An error occurred. Please try again.");
    }
  };

  return (
    <Layout>
      <Container>
        <Header>Change Password for SH+ Assistant</Header>
        <Form onSubmit={handleChangePassword}>
          <WarnContainer>
            <WarnBody>
              <BoldText>Important:</BoldText> The password you have set here is for your SH+ Assistant and <BoldText>does not change</BoldText> your SH+ password.
            </WarnBody>
            <WarnBody>
              You will continue to use <BoldText>separate passwords</BoldText> for SH+ Assistant and SH+.
            </WarnBody>
          </WarnContainer>

          <Description>
            Insert your new password below. Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character.
          </Description>

          <InputLabel>SH+ Assistant New Password</InputLabel>
          <InputPasswordContainer>
          <Input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={handlePasswordChange}
            autoComplete="new-password"
            placeholder="Enter your password"
          />
            <EyeButton
              onClick={() => setShowPassword(!showPassword)}
              type="button"
            >
              {showPassword ? <EyeSlashIcon size={20} /> : <EyeIcon size={20} />}
            </EyeButton>
          </InputPasswordContainer>

          <PasswordRequirementContainer>
            <PasswordRequirement active={hasMinLength}>
            <TickContainer active={hasMinLength}>
              <StyledTickIcon
                as={TickIcon}
                active={hasMinLength}
              />
            </TickContainer>
              <span>8 characters</span>
            </PasswordRequirement>

            <PasswordRequirement active={hasUppercase}>
            <TickContainer active={hasUppercase}>
              <StyledTickIcon
                as={TickIcon}
                active={hasUppercase}
              />
            </TickContainer>
              <span>1 uppercase letter</span>
            </PasswordRequirement>

            <PasswordRequirement active={hasLowercase}>
            <TickContainer active={hasLowercase}>
              <StyledTickIcon
                as={TickIcon}
                active={hasLowercase}
              />
            </TickContainer>
              <span>1 lowercase letter</span>
            </PasswordRequirement>

            <PasswordRequirement active={hasNumber}>
            <TickContainer active={hasNumber}>
              <StyledTickIcon
                as={TickIcon}
                active={hasNumber}
              />
            </TickContainer>
              <span>1 number</span>
            </PasswordRequirement>

            <PasswordRequirement active={hasSpecialChar}>
            <TickContainer active={hasSpecialChar}>
              <StyledTickIcon
                as={TickIcon}
                active={hasSpecialChar}
              />
            </TickContainer>
              <span>1 special character</span>
            </PasswordRequirement>
          </PasswordRequirementContainer>

          <InputLabel>Confirm SH+ Assistant New Password</InputLabel>
          <InputPasswordContainer>
            <Input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              autoComplete="new-password"
              placeholder="Confirm your password"
            />
            <EyeButton
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              type="button"
            >
              {showConfirmPassword ? <EyeSlashIcon size={20} /> : <EyeIcon size={20} />}
            </EyeButton>
          </InputPasswordContainer>

          {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

          <Button
            disabled={!password || !confirmPassword || !isPasswordValid}
            type="submit"
          >
            Update Password
          </Button>
        </Form>
      </Container>
    </Layout>
  );
};

export default ChangePassword;
